import { logger } from "@/utils/logger";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Loader2, Smartphone, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getStoredInterestData } from '@/utils/interestDataUtils';
import { sessionManager } from '@/utils/sessionManager';
import { getUTMSource, getUTMCampaign } from '@/utils/cookieUtils';
import html2canvas from 'html2canvas';

interface SMSResponseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWaitingForResponse?: () => void;
  onUserEngaged?: (engaged: boolean) => void;
}

const SMSResponseModal: React.FC<SMSResponseModalProps> = ({
  open,
  onOpenChange,
  onWaitingForResponse,
  onUserEngaged,
}) => {
  const { language } = useLanguage();
  const { sessionData } = useDealerSession();
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [aiResponseReady, setAiResponseReady] = useState(false);
  const phoneRef = useRef(phone);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep ref updated
  useEffect(() => {
    phoneRef.current = phone;
  }, [phone]);

  // Signal engagement when user is typing or has input
  useEffect(() => {
    const engaged = isUserTyping || phone.length > 0;
    onUserEngaged?.(engaged);
  }, [isUserTyping, phone, onUserEngaged]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setPhone('');
      setPhoneError('');
      setIsWaitingForResponse(false);
      setAiResponseReady(false);
    }
  }, [open]);

  // Check if AI response is ready (typing indicator gone) while modal is open
  useEffect(() => {
    if (!open) return;
    
    const checkForResponse = setInterval(() => {
      const typingIndicator = document.querySelector('[data-typing-indicator]');
      if (!typingIndicator) {
        setAiResponseReady(true);
        clearInterval(checkForResponse);
      }
    }, 500);
    
    return () => clearInterval(checkForResponse);
  }, [open]);

  // Format phone number as user types (xxx-xxx-xxxx)
  const formatPhoneInput = (value: string): string => {
    let digits = value.replace(/\D/g, '');
    
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    
    const limited = digits.slice(0, 10);
    
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value);
    setPhone(formatted);
    
    if (phoneError) {
      setPhoneError('');
    }
  };

  const handleFocus = () => {
    setIsUserTyping(true);
  };

  const handleBlur = () => {
    setIsUserTyping(false);
  };

  const validatePhone = (): boolean => {
    const digits = phone.replace(/\D/g, '');
    
    if (digits.length !== 10) {
      setPhoneError(language === 'fr' 
        ? 'Veuillez entrer un num&eacute;ro de t&eacute;l&eacute;phone valide &agrave; 10 chiffres'
        : 'Please enter a valid 10-digit phone number');
      return false;
    }
    
    return true;
  };

  // Get vehicle data from multiple sources with fallbacks
  const getVehicleData = async () => {
    const interestData = await getStoredInterestData();
    if (interestData?.vehicle?.make || interestData?.vehicle?.model) {
      return interestData.vehicle;
    }
    
    try {
      const websiteVehicle = localStorage.getItem('Website_Current_Vehicle');
      if (websiteVehicle) {
        const parsed = JSON.parse(websiteVehicle);
        if (parsed?.make || parsed?.model) {
          return {
            make: parsed.make,
            model: parsed.model,
            year: parsed.year,
            stock: parsed.stock || parsed.stockNumber,
            vin: parsed.vin || parsed.VIN,
          };
        }
      }
    } catch (e) {
      logger.warn('Could not parse Website_Current_Vehicle', e);
    }
    
    try {
      const vdpData = localStorage.getItem('VDPjsonData');
      if (vdpData) {
        const parsed = JSON.parse(vdpData);
        if (parsed?.make || parsed?.model) {
          return {
            make: parsed.make,
            model: parsed.model,
            year: parsed.year,
            stock: parsed.stock || parsed.stockNumber,
            vin: parsed.vin || parsed.VIN,
          };
        }
      }
    } catch (e) {
      logger.warn('Could not parse VDPjsonData', e);
    }
    
    return null;
  };

  const captureAndSendSMS = async (phoneNumber: string) => {
    logger.log('📸 SMSResponseModal: captureAndSendSMS STARTED with phone:', phoneNumber);
    try {
      // Find the chat messages container
      const chatContainer = document.querySelector('.d2cbot-chat-container');
      logger.log('📸 SMSResponseModal: chatContainer found:', !!chatContainer);
      
      const scrollAreaViewport = chatContainer?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      logger.log('📸 SMSResponseModal: scrollAreaViewport found:', !!scrollAreaViewport);
      
      // Get the actual content container inside the viewport (same approach as useChatState)
      const contentContainer = scrollAreaViewport?.firstElementChild as HTMLElement;
      logger.log('📸 SMSResponseModal: contentContainer found:', !!contentContainer);
      
      if (!scrollAreaViewport || !contentContainer) {
        console.error('📸 SMSResponseModal: Could not find messages container - aborting');
        throw new Error('Could not find messages container');
      }

      // Store original styles to restore later
      const originalViewportStyle = {
        height: scrollAreaViewport.style.height,
        maxHeight: scrollAreaViewport.style.maxHeight,
        overflow: scrollAreaViewport.style.overflow,
        position: scrollAreaViewport.style.position,
      };
      const originalScrollTop = scrollAreaViewport.scrollTop;

      // Expand viewport to show all content (critical for capture)
      scrollAreaViewport.style.height = 'auto';
      scrollAreaViewport.style.maxHeight = 'none';
      scrollAreaViewport.style.overflow = 'visible';
      scrollAreaViewport.style.position = 'relative';
      scrollAreaViewport.scrollTop = 0;

      // Wait for layout to settle
      await new Promise(resolve => setTimeout(resolve, 300));

      logger.log('📸 SMSResponseModal: Starting html2canvas capture...');
      // Capture screenshot of the content container (not the viewport)
      const canvas = await html2canvas(contentContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: contentContainer.scrollWidth,
        height: contentContainer.scrollHeight,
        windowWidth: contentContainer.scrollWidth,
        windowHeight: contentContainer.scrollHeight,
        logging: false,
      });

      // Restore original styles
      scrollAreaViewport.style.height = originalViewportStyle.height;
      scrollAreaViewport.style.maxHeight = originalViewportStyle.maxHeight;
      scrollAreaViewport.style.overflow = originalViewportStyle.overflow;
      scrollAreaViewport.style.position = originalViewportStyle.position;
      scrollAreaViewport.scrollTop = originalScrollTop;
      logger.log('📸 SMSResponseModal: Canvas captured, creating blob...');

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create blob'));
        }, 'image/png', 0.95);
      });
      logger.log('📸 SMSResponseModal: Blob created, size:', blob.size);

      // Upload to storage
      const sessionId = `session-${Date.now()}`;
      const fileName = `response-${sessionId}.png`;
      logger.log('📸 SMSResponseModal: Uploading to storage:', fileName);

      const { error: uploadError } = await supabase.storage
        .from('conversation-screenshots')
        .upload(fileName, blob, {
          contentType: 'image/png',
          upsert: true,
        });

      if (uploadError) {
        console.error('📸 SMSResponseModal: Upload error:', uploadError);
        throw uploadError;
      }
      logger.log('📸 SMSResponseModal: Upload successful');

      // Get signed URL (1 hour expiry)
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('conversation-screenshots')
        .createSignedUrl(fileName, 3600);
      
      if (signedUrlError || !signedUrlData?.signedUrl) {
        console.error('📸 SMSResponseModal: Signed URL error:', signedUrlError);
        throw new Error('Failed to generate signed URL');
      }
      logger.log('📸 SMSResponseModal: Signed URL generated');

      // Get vehicle data
      const vehicleData = await getVehicleData();
      
      // Build message
      const conversationSummary = sessionManager.getCurrentSession()?.conversationSummary;
      const message = conversationSummary || (language === 'fr'
        ? 'Voici votre r&eacute;ponse.'
        : 'Here is your response.');

      // Build request body
      const requestBody: Record<string, string> = {
        toNumber: phoneNumber,
        message,
        dealerId: sessionData?.dealerId?.toString() || '2',
        lang: language === 'fr' ? 'fr' : 'en',
        leadType: 'D2C_Chatbot_SMS',
      };

      if (vehicleData?.make) requestBody.make = vehicleData.make;
      if (vehicleData?.model) requestBody.model = vehicleData.model;
      if (vehicleData?.year) requestBody.year = vehicleData.year.toString();
      if (vehicleData?.stock) requestBody.stockNumber = vehicleData.stock;
      if (vehicleData?.vin) requestBody.vin = vehicleData.vin;
      if (signedUrlData?.signedUrl) requestBody.attach = signedUrlData.signedUrl;
      
      // Add campaign data from UTM cookies (ignore Google organic)
      const utmSource = getUTMSource();
      const utmCampaign = getUTMCampaign();
      const isGoogleOrganic = utmSource?.toLowerCase() === 'google' && utmCampaign?.toLowerCase() === 'organic';
      if (utmSource && !isGoogleOrganic) requestBody.campaignSource = utmSource;
      if (utmCampaign && !isGoogleOrganic) requestBody.campaignName = utmCampaign;

      logger.log('📸 SMSResponseModal: Sending SMS via edge function...');
      const { data, error } = await supabase.functions.invoke('send-sms-through-d2c', {
        body: requestBody
      });

      if (error) {
        console.error('📸 SMSResponseModal: Edge function error:', error);
        throw error;
      }

      if (data?.success) {
        logger.log('📸 SMSResponseModal: SMS sent successfully!');
        toast.success(
          language === 'fr' 
            ? 'SMS envoy&eacute; avec succ&egrave;s!' 
            : 'SMS sent successfully!'
        );
        onOpenChange(false);
      } else {
        console.error('📸 SMSResponseModal: SMS send failed:', data?.error);
        throw new Error(data?.error || 'SMS sending failed');
      }
    } catch (error) {
      console.error('📸 SMSResponseModal: CATCH block error:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de l\'envoi du SMS' 
          : 'Error sending SMS'
      );
    } finally {
      logger.log('📸 SMSResponseModal: FINALLY block - resetting state');
      setIsSubmitting(false);
      setIsWaitingForResponse(false);
    }
  };

  const handleSubmit = async () => {
    logger.log('📱 SMSResponseModal: handleSubmit called, aiResponseReady:', aiResponseReady);
    if (!validatePhone()) {
      logger.log('📱 SMSResponseModal: Phone validation failed');
      return;
    }
    
    setIsSubmitting(true);
    const formattedPhone = phone.replace(/\D/g, '');
    logger.log('📱 SMSResponseModal: Phone formatted:', formattedPhone);
    
    // If AI response is already ready, capture immediately
    if (aiResponseReady) {
      logger.log('📱 SMSResponseModal: AI response already ready, capturing in 500ms...');
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        logger.log('📱 SMSResponseModal: Timeout fired, calling captureAndSendSMS');
        captureAndSendSMS(formattedPhone);
      }, 500);
      return;
    }
    
    // Otherwise, wait for response
    logger.log('📱 SMSResponseModal: AI response NOT ready, waiting for typing indicator to disappear...');
    setIsWaitingForResponse(true);
    
    // Notify parent to release hold on AI response
    logger.log('📱 SMSResponseModal: Calling onWaitingForResponse to release AI hold');
    onWaitingForResponse?.();
    
    // Listen for AI response completion
    const handleResponseComplete = () => {
      logger.log('📱 SMSResponseModal: handleResponseComplete called, will capture in 1000ms...');
      // Small delay to ensure DOM is updated with the response
      setTimeout(() => {
        logger.log('📱 SMSResponseModal: Timeout fired after response complete, calling captureAndSendSMS');
        captureAndSendSMS(formattedPhone);
      }, 1000);
    };

    // Listen for typing indicator to disappear (AI response complete)
    let checkCount = 0;
    const checkForResponse = setInterval(() => {
      checkCount++;
      const typingIndicator = document.querySelector('[data-typing-indicator]');
      logger.log(`📱 SMSResponseModal: Check #${checkCount} - typing indicator present:`, !!typingIndicator);
      if (!typingIndicator) {
        logger.log('📱 SMSResponseModal: No typing indicator found, clearing interval and completing');
        clearInterval(checkForResponse);
        handleResponseComplete();
      }
    }, 500);

    // Timeout after 60 seconds
    setTimeout(() => {
      logger.log('📱 SMSResponseModal: 60 second timeout reached, isWaitingForResponse:', isWaitingForResponse);
      clearInterval(checkForResponse);
      if (isWaitingForResponse) {
        toast.error(language === 'fr' ? 'D&eacute;lai d&eacute;pass&eacute;' : 'Request timed out');
        setIsSubmitting(false);
        setIsWaitingForResponse(false);
      }
    }, 60000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting && phone.replace(/\D/g, '').length === 10) {
      handleSubmit();
    }
  };

  const titleHtml = language === 'fr'
    ? 'Recevez votre r&eacute;ponse par SMS!'
    : 'Get your response by SMS!';

  const phoneLabelHtml = language === 'fr'
    ? 'Num&eacute;ro de t&eacute;l&eacute;phone'
    : 'Phone number';

  const spinnerText = language === 'fr'
    ? "En pr&eacute;paration de l&apos;envoi de votre r&eacute;ponse par SMS..."
    : 'Preparing to send your response by SMS...';

  // Find the chat container to render the modal inside it
  const chatContainer = typeof document !== 'undefined' 
    ? document.querySelector('.d2cbot-chat-container') 
    : null;

  if (!open) return null;

  // Render a contained modal within the chat app
  return ReactDOM.createPortal(
    <div className="absolute inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - only covers the chat container */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />
      
      {/* Modal content */}
      <div className="relative bg-background rounded-lg shadow-lg border p-6 mx-4 max-w-sm w-full animate-in fade-in-0 zoom-in-95">
        {/* Close button */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        
        {/* Header */}
        <div className="flex items-center gap-2 text-lg font-semibold mb-4">
          <Smartphone className="w-5 h-5 text-primary" />
          <span dangerouslySetInnerHTML={{ __html: titleHtml }} />
        </div>
        
        {/* Content */}
        <div className="space-y-4">
          {isSubmitting ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p 
                className="text-sm text-muted-foreground text-center"
                dangerouslySetInnerHTML={{ __html: spinnerText }}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="sms-response-phone" dangerouslySetInnerHTML={{ __html: phoneLabelHtml }} />
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  id="sms-response-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={handlePhoneChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="xxx-xxx-xxxx"
                  className={`flex-1 text-base ${phoneError ? 'border-destructive' : ''}`}
                  autoFocus
                />
                <Button
                  type="button"
                  size="icon"
                  onClick={handleSubmit}
                  disabled={isSubmitting || phone.replace(/\D/g, '').length !== 10}
                  className="d2cbot-btn-square bg-primary hover:bg-primary/90"
                  aria-label={isSubmitting ? (language === 'fr' ? 'Envoi...' : 'Sending...') : (language === 'fr' ? 'Envoyer' : 'Send')}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  )}
                </Button>
              </div>
              {phoneError && (
                <p 
                  className="text-sm text-destructive" 
                  dangerouslySetInnerHTML={{ __html: phoneError }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    chatContainer || document.body
  );
};

export default SMSResponseModal;
